// frontend/src/components/UI/DoctorCallButton.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DoctorCallButtonProps {
  doctorPhone?: string;
  clinicPhone?: string;
  /** Скрыть кнопку полностью (например, врачи-партнёры) */
  hidden?: boolean;
  /** Стиль кнопки: 'icon' — только иконка, 'full' — иконка + текст */
  variant?: 'icon' | 'full';
  className?: string;
}

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const DoctorCallButton: React.FC<DoctorCallButtonProps> = ({
  doctorPhone,
  clinicPhone,
  hidden = false,
  variant = 'icon',
  className,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const phone = doctorPhone || clinicPhone;
  if (hidden || !phone) return null;

  const isPersonal = Boolean(doctorPhone);

  const handleClick = () => {
    if (isPersonal) {
      setModalOpen(true);
    } else {
      window.location.href = `tel:${phone}`;
    }
  };

  const buttonBase =
    'border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] flex items-center justify-center rounded-xl hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors shrink-0';

  const iconCls = 'w-6 h-6 text-[#454545] dark:text-[#e4e4e4]';

  return (
    <>
      <button
        onClick={handleClick}
        title={t('doctors.call')}
        className={className ?? (
          variant === 'full'
            ? `${buttonBase} flex-1 gap-2 h-14 px-4 text-[#0c63e9] dark:text-[#599bff] font-medium text-base`
            : `${buttonBase} p-4`
        )}
      >
        <PhoneIcon className={variant === 'full' ? 'w-6 h-6 shrink-0' : iconCls} />
        {variant === 'full' && (
          <span>{t('doctors.call')}</span>
        )}
      </button>

      {/* Модальное предупреждение для личного телефона врача */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 dark:bg-black/60"
            onClick={() => setModalOpen(false)}
          />

          {/* Card */}
          <div className="relative z-10 bg-[#fdfdfd] dark:bg-[#1f1f1f] rounded-2xl shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)] max-w-sm w-full p-6 flex flex-col gap-5">
            {/* Close */}
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-4 right-4 text-[#454545] dark:text-[#e4e4e4] hover:text-[#0c63e9] dark:hover:text-[#599bff] transition-colors"
              aria-label={t('common.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#d9e5f1] dark:bg-[#192e4d] flex items-center justify-center shrink-0">
                <PhoneIcon className="w-6 h-6 text-[#0c63e9] dark:text-[#599bff]" />
              </div>
              <h2 className="text-[#454545] dark:text-[#e4e4e4] font-semibold text-lg leading-snug">
                {t('doctorCall.modal.title')}
              </h2>
            </div>

            {/* Блок администратора */}
            {clinicPhone && (
              <div className="flex flex-col gap-2">
                <p className="text-[#454545] dark:text-[#e4e4e4] text-sm leading-relaxed">
                  {t('doctorCall.modal.adminHint')}
                </p>
                <div className="bg-[#edeef2] dark:bg-[#192e4d] rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="font-medium text-[#0c63e9] dark:text-[#599bff] text-base tracking-wide">
                    {clinicPhone}
                  </span>
                  <button
                    onClick={() => { setModalOpen(false); window.location.href = `tel:${clinicPhone}`; }}
                    className="h-9 px-4 rounded-lg bg-[#0c63e9] dark:bg-[#004dc3] text-[#eeeef0] text-sm font-medium hover:bg-[#0a54c7] transition-colors flex items-center justify-center gap-2"
                  >
                    <PhoneIcon className="w-4 h-4" />
                    {t('doctorCall.modal.callAdmin')}
                  </button>
                </div>
              </div>
            )}

            {/* Разделитель */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[rgba(160,195,219,0.5)] dark:bg-[rgba(30,50,64,0.75)]" />
              <span className="text-xs text-[#454545]/60 dark:text-[#e4e4e4]/40 shrink-0">
                {t('doctorCall.modal.or')}
              </span>
              <div className="flex-1 h-px bg-[rgba(160,195,219,0.5)] dark:bg-[rgba(30,50,64,0.75)]" />
            </div>

            {/* Блок личного телефона */}
            <div className="flex flex-col gap-2">
              <p className="text-[#454545] dark:text-[#e4e4e4] text-sm leading-relaxed">
                {t('doctorCall.modal.doctorHint')}
              </p>
              <div className="bg-[#edeef2] dark:bg-[#192e4d] rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="font-medium text-[#0c63e9] dark:text-[#599bff] text-base tracking-wide">
                  {doctorPhone}
                </span>
                <button
                  onClick={() => { setModalOpen(false); window.location.href = `tel:${doctorPhone}`; }}
                  className="h-9 px-4 rounded-lg border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] text-[#454545] dark:text-[#e4e4e4] text-sm font-medium hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors flex items-center justify-center gap-2"
                >
                  <PhoneIcon className="w-4 h-4" />
                  {t('doctorCall.modal.callDoctor')}
                </button>
              </div>
            </div>

            {/* Закрыть */}
            <button
              onClick={() => setModalOpen(false)}
              className="h-10 rounded-xl border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] text-[#454545] dark:text-[#e4e4e4] text-sm font-medium hover:bg-[#d9e5f1] dark:hover:bg-[#192e4d] transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DoctorCallButton;
